import React from 'react';
import { ExponentialProgressBarProps } from '../types';

/**
 * ExponentialProgressBar Component
 * 
 * Displays a visual progress bar with exponential scaling.
 * The component applies an exponential function to the progress value,
 * making early progress appear faster and later progress slower.
 * This can be useful for tasks where difficulty increases exponentially.
 * 
 * @param progress - A number between 0 and 1 representing completion percentage
 * @param exponent - Optional exponent value to control the curve (default: 0.5)
 * @param className - Optional custom class name to apply to the container
 * @param showPercentage - Whether to show the percentage text (default: true)
 * @param barColor - Custom color for the progress bar (default: 'bg-primary-light')
 */
export const ExponentialProgressBar: React.FC<ExponentialProgressBarProps> = ({
  progress,
  exponent = 0.7,
  className = '',
  barColor = 'bg-primary-verylight'
}) => {
  // Ensure progress is between 0 and 1
  const clampedProgress = Math.max(0, Math.min(1, progress));

  // Apply exponential function to the progress
  // For exponent < 1: progress appears faster at the beginning, slower at the end
  // For exponent > 1: progress appears slower at the beginning, faster at the end
  const exponentialProgress = Math.pow(clampedProgress, exponent);

  const displayWidth = Math.round(exponentialProgress * 100);

  return (
    <div>
      <div
        className={`w-full h-4 bg-gray-200 rounded-full overflow-hidden ${className}`}
        role="progressbar"
        aria-valuenow={exponentialProgress}
        aria-valuemin={0}
        aria-valuemax={1}
      >
        <div
          className={`h-full ${barColor} progress-bar-dynamic duration-500 ease-out rounded-full`}
          style={{ width: `${displayWidth}%` }}
        />
      </div>
    </div>
  );
};